import React, { useEffect, useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    CopyOutlined,
    CheckOutlined,
    SaveOutlined,
    CloseOutlined
} from "@ant-design/icons";
import { Col, Row, message, Button, Input } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { capitalizeFirstLetter, copyToClipboard } from "../../../../utils/functions";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import CommonDialog from "../../../global/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import Spinner from "../../../global/spinner";

const EnhanceZapierDetails = ({
    zapierAccountDetail,
    setShowDetail,
    keyParams
}) => {
    const [inputType, setInputType] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [destinations, setDestinations] = useState([]);
    const [webhookLinks, setWebhookLinks] = useState({});
    const [copyClipboardTextPassword, setCopyClipboardTextPassword] = useState(false);
    const [copyClipboardTextEmail, setCopyClipboardTextEmail] = useState(false);
    const [destinationPasswordVisible, setDestinationPasswordVisible] = useState({});

    const queryClient = useQueryClient()
    const saveZapierConfigMutation = useGenericMutation({
        endpoint: "zapier",
        onSuccess: () => {
            message.success("Zapier configuration saved successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.zapier.lists({ ...keyParams }),
                type: "active"
            })
            setShowDetail(false)
        },
        onError: (error) => {
            message.error(error?.data?.error?.message || "Failed to save configuration");
        }
    });

    const saveZapierSetupCompleteMutation = useGenericMutation({
        endpoint: "zapier/zap/setup/completed",
        onSuccess: () => {
            message.success("Zapier status updated successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.zapier.lists({ ...keyParams }),
                type: "active"
            })
            setShowDetail(false);
        },
        onError: (error) => {
            message.error(error?.response?.data?.message || "Failed to update status");
        }
    });

    useEffect(() => {
        if (zapierAccountDetail?.destinations) {
            setDestinations((prevDestinations) => [
                ...prevDestinations,
                ...zapierAccountDetail.destinations,
            ]);
        }
    }, [zapierAccountDetail?.destinations]);


    useEffect(() => {
        const updatedLinks = {};

        destinations.forEach((des) => {
            updatedLinks[des.name] = des?.webhookUrl;
        });

        setWebhookLinks((prevWebhookLinks) => ({
            ...prevWebhookLinks,
            ...updatedLinks,
        }));
    }, [destinations]);

    const saveZapierSetup = async (destinationName) => {
        try {
            await saveZapierConfigMutation.mutateAsync({
                id: zapierAccountDetail._id,
                email: zapierAccountDetail.email,
                password: zapierAccountDetail.password,
                destinations,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleZapierSetusComplete = async () => {
        try {
            await saveZapierSetupCompleteMutation.mutateAsync({
                id: zapierAccountDetail._id,
                status: !zapierAccountDetail.zapCreatedByAdmin,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleWebhookLinkChange = (e, destinationName) => {
        setDestinations((prevDestinations) => {
            return prevDestinations.map((destination) => {
                if (destination.name === destinationName) {
                    return {
                        ...destination,
                        webhookUrl: e.target.value,
                    };
                }
                return destination;
            });
        });

        setWebhookLinks({
            ...webhookLinks,
            [destinationName]: e.target.value,
        });
    };

    const togglePassword = () => {
        setInputType(!inputType);
    };

    const toggleDestinationPassword = (idx) => {
        setDestinationPasswordVisible(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const toggleCopyIcon = (idx, type) => {
        const copyStateSetter =
            type === "password" ? setCopyClipboardTextPassword : setCopyClipboardTextEmail;

        copyStateSetter((prevCopyStates) => {
            return { ...prevCopyStates, [idx]: !prevCopyStates[idx] };
        });
    };

    const copyToClipboardHandler = async (text, idx, type) => {
        const success = await copyToClipboard(text);
        if (success) {
            toggleCopyIcon(idx, type);
            message.success("Copied to clipboard");
            setTimeout(() => {
                toggleCopyIcon(idx, type);
            }, 1000);
        } else {
            message.error("Failed to copy");
        }
    };

    const getIconForField = (label) => {
        const iconMap = {
            User: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Password: <EyeOutlined />,
            "Enter Zapier Webhook Link": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-4 sm:mb-6">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(16px, 4vw, 18px)" } })}
            <span style={{ fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const FieldItem = ({ label, value, children, idx, total, isDivider = true }) => (
        <Col span={24} className={cn(isDivider && "border-b border-gray-200", "py-3 sm:py-4", idx === total - 1 && "!border-b-0")}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-1 sm:gap-4">
                <span className="text-[11px] sm:text-sm text-gray-500 sm:text-gray-600 flex items-center gap-2 font-medium">
                    <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" }}>
                        {getIconForField(label)}
                    </span>
                    {label}
                </span>
                {children ? children : (
                    <span className="text-sm sm:text-base font-semibold text-gray-900 break-words w-full sm:w-auto text-left sm:text-right">
                        {value || "—"}
                    </span>
                )}
            </div>
        </Col>
    );

    if (!zapierAccountDetail) return null;

    return (
        <>
            <div className="p-2 sm:p-6" style={{ backgroundColor: "#f5f7fa", minHeight: "100%" }}>
                <Row gutter={[16, 16]}>
                    {/* Account Information - Combined Customer & Zapier Info */}
                    <Col span={24}>
                        <CommonCard cardHeadBodyClassName={{ head: "!px-6 !py-4", body: "!px-6 !py-4 !pb-4" }}>
                            <SectionHeader icon={<UserOutlined />} title="Account Information" />
                            <Row gutter={[24, 24]}>
                                {/* Customer Information Column */}
                                <Col xs={24} lg={12}>
                                    <div className="lg:border-r border-gray-200 lg:pr-6 h-full">
                                        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-4 flex items-center gap-2">
                                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)" }} />
                                            Customer Details
                                        </h4>
                                        <Row>
                                            {[
                                                { label: "User", value: zapierAccountDetail?.userDetails?.name },
                                                { label: "Email", value: zapierAccountDetail?.userDetails?.email },
                                                { label: "Phone", value: zapierAccountDetail?.userDetails?.phone },
                                            ].map((item, idx, arr) => (
                                                <FieldItem
                                                    key={item.label}
                                                    label={item.label}
                                                    value={item.value}
                                                    idx={idx}
                                                    total={arr.length}
                                                />
                                            ))}
                                        </Row>
                                    </div>
                                </Col>

                                {/* Zapier Information Column */}
                                <Col xs={24} lg={12}>
                                    <div className="lg:pl-2">
                                        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-4 flex items-center gap-2">
                                            <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)" }} />
                                            Zapier Credentials
                                        </h4>
                                        <Row>
                                            {/* Email */}
                                            <FieldItem label="Email" idx={0} total={2}>
                                                <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                                    <span className="text-sm sm:text-base font-semibold text-gray-900 break-all">
                                                        {zapierAccountDetail?.email}
                                                    </span>
                                                    {copyClipboardTextEmail[zapierAccountDetail?._id] ? (
                                                        <CheckOutlined className="text-success" style={{ fontSize: "16px" }} />
                                                    ) : (
                                                        <CopyOutlined
                                                            className="text-success cursor-pointer hover:scale-110 transition-transform"
                                                            style={{ fontSize: "16px" }}
                                                            title="Copy Email"
                                                            onClick={() => copyToClipboardHandler(zapierAccountDetail?.email, zapierAccountDetail?._id, "email")}
                                                        />
                                                    )}
                                                </div>
                                            </FieldItem>

                                            {/* Password */}
                                            <FieldItem label="Password" idx={1} total={2}>
                                                <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                                    <span
                                                        className="text-sm sm:text-base font-semibold text-gray-900"
                                                        style={inputType ? { filter: "blur(5px)" } : {}}
                                                    >
                                                        {zapierAccountDetail?.password}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        {inputType ? (
                                                            <EyeOutlined
                                                                className="text-primary cursor-pointer hover:scale-110 transition-transform"
                                                                style={{ fontSize: "16px" }}
                                                                title="Show Password"
                                                                onClick={togglePassword}
                                                            />
                                                        ) : (
                                                            <EyeInvisibleOutlined
                                                                className="text-primary cursor-pointer hover:scale-110 transition-transform"
                                                                style={{ fontSize: "16px" }}
                                                                title="Hide Password"
                                                                onClick={togglePassword}
                                                            />
                                                        )}
                                                        {copyClipboardTextPassword[zapierAccountDetail?._id] ? (
                                                            <CheckOutlined className="text-success" style={{ fontSize: "16px" }} />
                                                        ) : (
                                                            <CopyOutlined
                                                                className="text-success cursor-pointer hover:scale-110 transition-transform"
                                                                style={{ fontSize: "16px" }}
                                                                title="Copy Password"
                                                                onClick={() => copyToClipboardHandler(zapierAccountDetail?.password, zapierAccountDetail?._id, "password")}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </FieldItem>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </CommonCard>
                    </Col>
                </Row>

                {/* Destination Information Cards */}
                {zapierAccountDetail?.destinations?.map((destination, idx) => (
                    <Row key={idx} gutter={[16, 16]} className="mt-4 sm:mt-6">
                        <Col span={24}>
                            <CommonCard cardHeadBodyClassName={{ head: "!px-6 !py-4", body: "!px-6 !py-4 !pb-4" }}>
                                <SectionHeader
                                    icon={<InfoCircleOutlined />}
                                    title={`Customer ${capitalizeFirstLetter(destination.name)} Information`}
                                />
                                <Row>
                                    {/* Email */}
                                    <FieldItem label="Email" idx={0} total={4}>
                                        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                            <span className="text-sm sm:text-base font-semibold text-gray-900 break-all">{destination.email}</span>
                                            {copyClipboardTextEmail[idx] ? (
                                                <CheckOutlined className="text-success" style={{ fontSize: "16px" }} />
                                            ) : (
                                                <CopyOutlined
                                                    className="text-success cursor-pointer hover:scale-110 transition-transform"
                                                    style={{ fontSize: "16px" }}
                                                    title="Copy Email"
                                                    onClick={() => copyToClipboardHandler(destination.email, idx, "email")}
                                                />
                                            )}
                                        </div>
                                    </FieldItem>

                                    {/* Password */}
                                    <FieldItem label="Password" idx={1} total={4}>
                                        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                            <span
                                                className="text-sm sm:text-base font-semibold text-gray-900"
                                                style={!destinationPasswordVisible[idx] ? { filter: "blur(5px)" } : {}}
                                            >
                                                {destination.password}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {destinationPasswordVisible[idx] ? (
                                                    <EyeInvisibleOutlined
                                                        className="text-primary cursor-pointer hover:scale-110 transition-transform"
                                                        style={{ fontSize: "16px" }}
                                                        title="Hide Password"
                                                        onClick={() => toggleDestinationPassword(idx)}
                                                    />
                                                ) : (
                                                    <EyeOutlined
                                                        className="text-primary cursor-pointer hover:scale-110 transition-transform"
                                                        style={{ fontSize: "16px" }}
                                                        title="Show Password"
                                                        onClick={() => toggleDestinationPassword(idx)}
                                                    />
                                                )}
                                                {copyClipboardTextPassword[idx] ? (
                                                    <CheckOutlined className="text-success" style={{ fontSize: "16px" }} />
                                                ) : (
                                                    <CopyOutlined
                                                        className="text-success cursor-pointer hover:scale-110 transition-transform"
                                                        style={{ fontSize: "16px" }}
                                                        title="Copy Password"
                                                        onClick={() => copyToClipboardHandler(destination.password, idx, "password")}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </FieldItem>

                                    {/* Webhook Link Input */}
                                    <FieldItem label="Enter Zapier Webhook Link" idx={2} total={4}>
                                        <Input
                                            placeholder="Enter Zapier Webhook Link"
                                            value={webhookLinks[destination.name] || ""}
                                            onChange={(e) => handleWebhookLinkChange(e, destination.name)}
                                            className="w-full focus:!border-primary h-9 sm:h-10 text-xs sm:text-sm"
                                        />
                                    </FieldItem>

                                    {/* Save Button */}
                                    <Col span={24} className="pt-4">
                                        <Button
                                            type="default"
                                            icon={saveZapierConfigMutation.isPending ? null : <SaveOutlined />}
                                            onClick={() => saveZapierSetup(destination.name)}
                                            className="w-full h-9 sm:h-10 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white group text-xs sm:text-sm font-medium"
                                        >
                                            {saveZapierConfigMutation.isPending && <Spinner className={"group-hover:!border-white border-primary"} />}
                                            Save
                                        </Button>
                                    </Col>
                                </Row>
                            </CommonCard>
                        </Col>
                    </Row>
                ))}

                {/* Complete Setup Button */}
                <Row className="mt-4 sm:mt-6">
                    <Col span={24}>
                        <CommonCard>
                            <Button
                                type="default"
                                icon={zapierAccountDetail?.zapCreatedByAdmin === false ? <CheckOutlined /> : <CloseOutlined />}
                                onClick={() => {
                                    setShowModal(true);
                                }}
                                className={cn(
                                    "w-full h-9 sm:h-10 text-xs sm:text-sm font-medium transition-all duration-200",
                                    zapierAccountDetail?.zapCreatedByAdmin === false
                                        ? "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white"
                                        : "border !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white"
                                )}
                            >
                                {zapierAccountDetail?.zapCreatedByAdmin === false
                                    ? "Complete Zapier Setup"
                                    : "Mark Pending"}
                            </Button>
                        </CommonCard>
                    </Col>
                </Row>
            </div>

            {showModal && (
                <CommonDialog
                    title={zapierAccountDetail?.zapCreatedByAdmin ? "Mark Pending" : "Zapier Setup Completion"}
                    description={zapierAccountDetail?.zapCreatedByAdmin ? "Are you sure you want to mark this Zapier Setup as pending?" : "Are you sure you want to complete Zapier Setup?"}
                    open={showModal}
                    onCancel={() => setShowModal(false)}
                    onOk={handleZapierSetusComplete}
                    isConfirmButtonLoading={saveZapierConfigMutation.isPending}
                />
            )}
        </>
    );
};

export default EnhanceZapierDetails;